import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';
import { errorHandlingService } from './errorHandlingService';

const API_BASE_URL = `${ENV.API_URL}/api/support`;

const getToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('supportService: error getting Supabase token', e);
    return '';
  }
};

const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    const message = (data && (data.message || data.error)) || text || 'Unknown error';
    errorHandlingService.logError(new Error(message), `supportService [${response.status}]`);
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

const requireString = (v: any, name: string) => {
  if (!v || typeof v !== 'string') throw new Error(`${name} is required`);
};

export type SupportDomainStatus = 'pending' | 'verified' | 'active' | 'disabled';

export type SupportDomainConfig = {
  id: string;
  organization_id: string;
  domain: string;
  allowed_origins: string[];
  status: SupportDomainStatus;
  verified_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type SupportKbArticle = {
  id: string;
  organization_id: string;
  title: string;
  slug: string;
  content?: string;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
};

export type SupportTicketListItem = {
  id: string;
  status: 'new' | 'open' | 'waiting' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  subject?: string | null;
  created_at?: string;
  assigned_to?: string | null;
  end_user_id?: string | null;
  conversation_id?: string | null;
  end_user?: { email?: string | null; name?: string | null } | null;
};

export type SupportTicketMessage = {
  id: string;
  conversation_id?: string | null;
  ticket_id?: string | null;
  sender_type: 'end_user' | 'assistant' | 'agent';
  content: string;
  visibility?: 'public' | 'internal';
  channel?: 'widget' | 'email' | 'api';
  created_at?: string;
};

export type OrgMemberListItem = {
  id: string;
  user_id: string;
  email?: string | null;
  role?: string | null;
};

export type SupportTicketDetail = SupportTicketListItem & {
  description?: string;
  end_user_id?: string | null;
  conversation_id?: string | null;
  assigned_to?: string | null;
  messages?: SupportTicketMessage[];
};

export const supportService = {
  async getDomain(organizationId: string): Promise<SupportDomainConfig | null> {
    requireString(organizationId, 'organizationId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/domain`);
      url.searchParams.set('organization_id', organizationId);
      const res = await fetch(url.toString(), { method: 'GET', headers });
      const body = await handleResponse<{ data: SupportDomainConfig | null }>(res);
      return body?.data || null;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.getDomain');
      throw e;
    }
  },

  async getKbArticle(params: { id: string; organization_id: string }): Promise<SupportKbArticle> {
    requireString(params?.id, 'id');
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/kb/articles/${encodeURIComponent(params.id)}`);
      url.searchParams.set('organization_id', params.organization_id);
      const res = await fetch(url.toString(), { method: 'GET', headers });
      const body = await handleResponse<{ data: SupportKbArticle }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.getKbArticle');
      throw e;
    }
  },

  async upsertDomain(params: { organization_id: string; domain: string; allowed_origins: string[] }): Promise<SupportDomainConfig> {
    requireString(params?.organization_id, 'organization_id');
    requireString(params?.domain, 'domain');
    if (!Array.isArray(params?.allowed_origins)) throw new Error('allowed_origins is required');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/domain`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportDomainConfig }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.upsertDomain');
      throw e;
    }
  },

  async verifyDomain(params: { organization_id: string }): Promise<SupportDomainConfig> {
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/domain/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportDomainConfig }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.verifyDomain');
      throw e;
    }
  },

  async listKbArticles(params: { organization_id: string; limit?: number; offset?: number }): Promise<SupportKbArticle[]> {
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/kb/articles`);
      url.searchParams.set('organization_id', params.organization_id);
      if (typeof params.limit === 'number') url.searchParams.set('limit', String(params.limit));
      if (typeof params.offset === 'number') url.searchParams.set('offset', String(params.offset));
      const res = await fetch(url.toString(), { method: 'GET', headers });
      const body = await handleResponse<{ data: SupportKbArticle[] }>(res);
      return Array.isArray(body.data) ? body.data : [];
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.listKbArticles');
      throw e;
    }
  },

  async createKbArticle(params: { organization_id: string; title: string; slug: string; content: string; is_published?: boolean }): Promise<SupportKbArticle> {
    requireString(params?.organization_id, 'organization_id');
    requireString(params?.title, 'title');
    requireString(params?.slug, 'slug');
    requireString(params?.content, 'content');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/kb/articles`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportKbArticle }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.createKbArticle');
      throw e;
    }
  },

  async updateKbArticle(id: string, params: { organization_id: string; title?: string; slug?: string; content?: string; is_published?: boolean }): Promise<SupportKbArticle> {
    requireString(id, 'id');
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/kb/articles/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportKbArticle }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.updateKbArticle');
      throw e;
    }
  },

  async deleteKbArticle(id: string, organizationId: string): Promise<{ success: boolean }> {
    requireString(id, 'id');
    requireString(organizationId, 'organizationId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/kb/articles/${encodeURIComponent(id)}`);
      url.searchParams.set('organization_id', organizationId);
      const res = await fetch(url.toString(), { method: 'DELETE', headers });
      return handleResponse<{ success: boolean }>(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.deleteKbArticle');
      throw e;
    }
  },

  async listTickets(params: {
    organization_id: string;
    status?: string;
    assigned_to?: string;
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<SupportTicketListItem[]> {
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/tickets`);
      url.searchParams.set('organization_id', params.organization_id);
      if (params.status) url.searchParams.set('status', params.status);
      if (params.assigned_to) url.searchParams.set('assigned_to', params.assigned_to);
      if (params.q) url.searchParams.set('q', params.q);
      if (typeof params.limit === 'number') url.searchParams.set('limit', String(params.limit));
      if (typeof params.offset === 'number') url.searchParams.set('offset', String(params.offset));
      const res = await fetch(url.toString(), { method: 'GET', headers });
      const body = await handleResponse<{ data: SupportTicketListItem[] }>(res);
      return Array.isArray(body.data) ? body.data : [];
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.listTickets');
      throw e;
    }
  },

  async getTicket(id: string, organizationId: string): Promise<SupportTicketDetail> {
    requireString(id, 'id');
    requireString(organizationId, 'organizationId');
    try {
      const headers = await authHeaders();
      const url = new URL(`${API_BASE_URL}/admin/tickets/${encodeURIComponent(id)}`);
      url.searchParams.set('organization_id', organizationId);
      const res = await fetch(url.toString(), { method: 'GET', headers });
      const body = await handleResponse<{ data: SupportTicketDetail }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.getTicket');
      throw e;
    }
  },

  async updateTicket(id: string, params: { organization_id: string; status?: SupportTicketListItem['status']; priority?: SupportTicketListItem['priority'] }): Promise<SupportTicketDetail> {
    requireString(id, 'id');
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/tickets/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportTicketDetail }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.updateTicket');
      throw e;
    }
  },

  async listOrgMembers(organizationId: string): Promise<OrgMemberListItem[]> {
    requireString(organizationId, 'organizationId');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${ENV.API_URL}/api/crm/organizations/${encodeURIComponent(organizationId)}/members`, { method: 'GET', headers });
      const body = await handleResponse<{ data: OrgMemberListItem[] }>(res);
      return Array.isArray(body.data) ? body.data : [];
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.listOrgMembers');
      throw e;
    }
  },

  async assignTicket(id: string, params: { organization_id: string; assigned_to: string | null }): Promise<SupportTicketDetail> {
    requireString(id, 'id');
    requireString(params?.organization_id, 'organization_id');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/tickets/${encodeURIComponent(id)}/assign`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportTicketDetail }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.assignTicket');
      throw e;
    }
  },

  async replyToTicket(id: string, params: { organization_id: string; content: string }): Promise<SupportTicketMessage> {
    requireString(id, 'id');
    requireString(params?.organization_id, 'organization_id');
    requireString(params?.content, 'content');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/tickets/${encodeURIComponent(id)}/reply`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportTicketMessage }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.replyToTicket');
      throw e;
    }
  },

  async addTicketNote(id: string, params: { organization_id: string; content: string }): Promise<SupportTicketMessage> {
    requireString(id, 'id');
    requireString(params?.organization_id, 'organization_id');
    requireString(params?.content, 'content');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE_URL}/admin/tickets/${encodeURIComponent(id)}/notes`, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
      const body = await handleResponse<{ data: SupportTicketMessage }>(res);
      return body.data;
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'supportService.addTicketNote');
      throw e;
    }
  },
};
